import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Icon, Input, Modal, Select, Spin, Tag, Upload, message } from 'antd'
import Html5QrcodePlugin from '../../components/QrReader'
import ImagePreviewModal from '../../components/ImagePreviewModal'
import LocationPickerModal from '../../components/LocationPickerModal'
import { findAssetByScan, searchAssets, uploadPhotos, errorMessage } from './api'
import { assetLabel, assetPlace, compressImage, getCurrentPosition, parseQrText } from './helpers'

const SEARCH_DEBOUNCE_MS = 300

const AssetConditionForm = ({ form, conditions, location, client, addedAssetIds, onAdd }) => {
  const { getFieldDecorator, getFieldValue } = form
  const [assetOptions, setAssetOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [entryMode, setEntryMode] = useState('MANUAL')
  const [scanOpen, setScanOpen] = useState(false)
  const [scanLoading, setScanLoading] = useState(false)
  const [geo, setGeo] = useState(null)
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState('')
  const [photos, setPhotos] = useState([])
  const [uploading, setUploading] = useState(0)
  const [previewIndex, setPreviewIndex] = useState(null)
  const [mapOpen, setMapOpen] = useState(false)
  const searchTimer = useRef(null)

  const selectedCondition = conditions.find((c) => String(c._id) === String(getFieldValue('condition')))
  const photoRequired = !!(selectedCondition && selectedCondition.isPhotoRequired)
  const geoRequired = !!(selectedCondition && selectedCondition.isGeoLocationRequired)

  const captureLocation = () => {
    setGeoLoading(true)
    setGeoError('')
    getCurrentPosition()
      .then((pos) => setGeo({ ...pos, source: 'GPS' }))
      .catch((e) => { setGeo(null); setGeoError(e.message) })
      .finally(() => setGeoLoading(false))
  }

  useEffect(() => {
    captureLocation()
    return () => clearTimeout(searchTimer.current)
  }, [])

  // Assets are scoped to the selected location / sub location; drop any selection made for another scope
  useEffect(() => {
    setAssetOptions([])
    setSelectedAsset(null)
    form.setFieldsValue({ asset: undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, client])

  const handleMapPicked = ({ latitude, longitude }) => {
    setGeo({ latitude, longitude, source: 'MAP' })
    setGeoError('')
    setMapOpen(false)
  }

  const chooseAsset = (asset, mode) => {
    if (addedAssetIds.includes(String(asset._id))) {
      message.error(`${asset.assetCode} is already added to this voucher`)
      return
    }
    setAssetOptions((prev) => prev.some((a) => a._id === asset._id) ? prev : [asset, ...prev])
    setSelectedAsset(asset)
    setEntryMode(mode)
    form.setFieldsValue({ asset: asset._id })
  }

  const handleSearch = (text) => {
    clearTimeout(searchTimer.current)
    if (!text || !text.trim() || !location) return
    searchTimer.current = setTimeout(() => {
      setSearching(true)
      searchAssets(text.trim(), { location, client })
        .then((data) => setAssetOptions(data || []))
        .catch((e) => message.error(errorMessage(e, 'Asset search failed')))
        .finally(() => setSearching(false))
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleAssetSelect = (id) => {
    const asset = assetOptions.find((a) => a._id === id)
    if (asset) chooseAsset(asset, 'MANUAL')
  }

  const handleScanResult = (text) => {
    setScanOpen(false)
    const parsed = parseQrText(text)
    if (!parsed) {
      message.error('Invalid QR!')
      return
    }
    setScanLoading(true)
    findAssetByScan({ ...parsed, location, client })
      .then((asset) => chooseAsset(asset, 'QR'))
      .catch((e) => message.error(errorMessage(e, 'No asset found for this QR')))
      .finally(() => setScanLoading(false))
  }

  const beforeUpload = (file) => {
    if (!/^image\//.test(file.type)) {
      message.error('Only images can be uploaded')
      return false
    }
    setUploading((n) => n + 1)
    compressImage(file)
      .then((compressed) => uploadPhotos([compressed]))
      .then((uploaded) => setPhotos((prev) => [...prev, ...uploaded.map((p, i) => ({ ...p, uid: `${Date.now()}-${i}` }))]))
      .catch((e) => message.error(errorMessage(e, 'Photo upload failed')))
      .finally(() => setUploading((n) => n - 1))
    return false
  }

  const reset = () => {
    form.resetFields()
    setSelectedAsset(null)
    setEntryMode('MANUAL')
    setPhotos([])
    captureLocation()
  }

  const handleAdd = () => {
    form.validateFields((err, values) => {
      if (err) return
      if (!location) {
        message.error('Please select a location first')
        return
      }
      if (!selectedAsset) {
        message.error('Please select an asset')
        return
      }
      if (addedAssetIds.includes(String(selectedAsset._id))) {
        message.error(`${selectedAsset.assetCode} is already added to this voucher`)
        return
      }
      if (uploading) {
        message.warning('Please wait for the photos to finish uploading')
        return
      }
      if (photoRequired && !photos.length) {
        message.error(`A photo is required for '${selectedCondition.name}'`)
        return
      }
      if (geoRequired && !geo) {
        message.error(`Geo location is required for '${selectedCondition.name}'. Capture the location and try again.`)
        return
      }
      onAdd({
        key: `${selectedAsset._id}-${Date.now()}`,
        asset: selectedAsset,
        condition: selectedCondition,
        entryMode,
        remarks: (values.remarks || '').trim(),
        latitude: geo ? geo.latitude : undefined,
        longitude: geo ? geo.longitude : undefined,
        geoSource: geo ? geo.source : undefined,
        photos: photos.map(({ name, mimeType, url }) => ({ name, mimeType, url })),
        createdAt: new Date().toISOString()
      })
      reset()
    })
  }

  return (
    <Spin spinning={scanLoading}>
      {!location && <Alert type='info' showIcon className='acv-scope-alert' message='Select a location (and sub location if needed) above to add records' />}
      <Form layout='vertical' className='acv-record-form'>
        <Form.Item label='Asset' required>
          <div className='acv-asset-row'>
            {getFieldDecorator('asset', {
              rules: [{ required: true, message: 'Asset is required!' }]
            })(
              <Select
                showSearch
                allowClear
                placeholder={location ? 'Search by asset code' : 'Select a location first'}
                disabled={!location}
                filterOption={false}
                notFoundContent={searching ? <Spin size='small' /> : null}
                onSearch={handleSearch}
                onSelect={handleAssetSelect}
                onChange={(value) => { if (!value) setSelectedAsset(null) }}
                className='acv-asset-select'
              >
                {assetOptions.map((asset) => (
                  <Select.Option key={asset._id} value={asset._id}>{assetLabel(asset)}</Select.Option>
                ))}
              </Select>
            )}
            <Button icon='qrcode' onClick={() => setScanOpen(true)} disabled={!location}>Scan QR</Button>
          </div>
          {selectedAsset && (
            <div className='acv-help'>
              {entryMode === 'QR' ? 'Scanned' : 'Selected'}: {assetLabel(selectedAsset)}
              {assetPlace(selectedAsset) ? ` (${assetPlace(selectedAsset)})` : ''}
            </div>
          )}
        </Form.Item>

        <Form.Item label='Condition'>
          {getFieldDecorator('condition', {
            rules: [{ required: true, message: 'Condition is required!' }]
          })(
            <Select placeholder='Select condition'>
              {conditions.map((c) => (
                <Select.Option key={c._id} value={c._id}>
                  {c.color && <span className='acv-color-dot' style={{ background: c.color }} />}
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item label='Remark'>
          {getFieldDecorator('remarks')(<Input.TextArea rows={2} placeholder='Remark' maxLength={500} />)}
        </Form.Item>

        <Form.Item label='Geo Location' required={geoRequired}>
          <div className='acv-geo-row'>
            <span>
              {geoLoading ? 'Getting location...' : geo
                ? `${geo.latitude.toFixed(6)}, ${geo.longitude.toFixed(6)}${geo.accuracy ? ` (±${Math.round(geo.accuracy)} m)` : ''}`
                : 'Not captured'}
            </span>
            {geo && <Tag>{geo.source === 'MAP' ? 'Picked on map' : 'GPS'}</Tag>}
            <Button size='small' icon='aim' loading={geoLoading} onClick={captureLocation}>
              {geo && geo.source === 'GPS' ? 'Refresh GPS' : 'Use GPS'}
            </Button>
            <Button size='small' icon='environment' onClick={() => setMapOpen(true)}>Pick on Map</Button>
          </div>
          {geoError && <Alert type='error' showIcon message={geoError} className='acv-geo-error' />}
        </Form.Item>

        <Form.Item label='Photos' required={photoRequired}>
          <Upload
            accept='image/*'
            listType='picture-card'
            multiple
            fileList={photos.map((p) => ({ uid: p.uid, name: p.name, status: 'done', url: p.url }))}
            beforeUpload={beforeUpload}
            onRemove={(file) => setPhotos((prev) => prev.filter((p) => p.uid !== file.uid))}
            onPreview={(file) => setPreviewIndex(Math.max(0, photos.findIndex((p) => p.uid === file.uid)))}
          >
            {uploading ? <Spin /> : (
              <div>
                <Icon type='camera' />
                <div className='ant-upload-text'>Add Photo</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Button type='primary' icon='plus' onClick={handleAdd} disabled={!!uploading || !location}>Add Record</Button>
      </Form>

      <LocationPickerModal
        visible={mapOpen}
        initialLat={geo ? geo.latitude : undefined}
        initialLng={geo ? geo.longitude : undefined}
        onOk={handleMapPicked}
        onCancel={() => setMapOpen(false)}
      />

      <ImagePreviewModal
        visible={previewIndex !== null}
        photos={photos}
        initialIndex={previewIndex || 0}
        onClose={() => setPreviewIndex(null)}
      />

      <Modal
        title='Scan QR Code'
        visible={scanOpen}
        onCancel={() => setScanOpen(false)}
        destroyOnClose
        centered
        footer={<Button onClick={() => setScanOpen(false)}>Close</Button>}
      >
        {scanOpen && <Html5QrcodePlugin fps={10} qrbox={250} disableFlip={false} qrCodeSuccessCallback={handleScanResult} />}
      </Modal>
    </Spin>
  )
}

export default Form.create({ name: 'acv_record' })(AssetConditionForm)
